import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  InputLabel,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import Modal, { CommonModalProps } from "..";
import FormInputText from "../../Forms/FormInputText/form-input-text";
import { useAppSelector } from "../../../Redux/redux-store";
import UploadCard, { UploadContext } from "../../Cards/UploadCard/upload-card";
import { ObjectType } from "../../../Core/Api/Types/object-type";
import { supportService, userService } from "../../../Core/Api/services";
import FormAutocomplete from "../../Forms/FormAutocomplete/form-autocomplete";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import { Item } from "../ModalSupportTicket/modal-support-ticket-zendesk";
import { GetFormIsValid } from "../../../Redux/Reducers/redux-slice-form-validation";
import WarningIcon from "@mui/icons-material/Warning";
import { IssuePriority } from "../../../Core/Api/Types/issue-priority";
import { useTranslation } from "react-i18next";
import { IsEmail } from "../../../Utils/helpers";

export type ModalReportIssueElementProps = {
  typology: string;
  objectId?: number;
  techInfos?: string;
  onConfirm?: (
    subject: string,
    subTypology: string,
    priority: IssuePriority,
    followers: string[],
    comment?: string,
    fileData?: File
  ) => void;
};

export type ModalReportIssueProps = CommonModalProps<ModalReportIssueElementProps>;

const ModalReportIssue = React.forwardRef<any, ModalReportIssueProps>((props, ref) => {
  const { t } = useTranslation();
  const { typology, objectId = null, closeModal, onConfirm, ...modalProps } = props;
  const { techInfos } = modalProps;
  const [description, setDescription] = useState<string>("");
  const [priority, setPriority] = useState<IssuePriority>(IssuePriority.Medium);
  const isValid = useAppSelector(GetFormIsValid("form-confirm"));
  const [fileData, setFileData] = useState<File>();
  const [jsonObject, setJsonObject] = useState<any>(null);
  const [subTypologyOptions, setSubTypologyOptions] = useState<Item[] | null>(null);
  const [subTypologyValue, setSubTypologyValue] = useState<Item | null>(null);
  const [subject, setSubject] = useState<string>("");
  const [alreadyReported, setAlreadyReported] = useState<boolean>(false);
  const [reportIds, setReportIds] = useState<number[] | null>(null);
  const [followersUsersOptions, setFollowersUsersOptions] = useState<string[]>([]);
  const [selectedFollowers, setSelectedFollowers] = useState<string[]>([]);

  const handleConfirmClick = () => {
    onConfirm && onConfirm(subject, subTypologyValue?.value!, priority, selectedFollowers, description, fileData);
    closeModal();
  };

  const handleCancelClick = () => {
    closeModal();
  };

  const handleConfirmAnywayClick = () => {
    supportService.GetZendeskFormFields().then((response) => {
      setJsonObject(response.data);
      setSubTypologyOptions(response.data?.["Sous-typologie " + typology] || []);
    });
    setAlreadyReported(false);
  };

  useEffect(() => {
    if (followersUsersOptions.length === 0)
      userService.GetAllEmailsAsStringList().then((emails) => {
        if (emails) setFollowersUsersOptions(emails);
      });

    if (objectId !== null) {
      supportService.GetIssueAlreadyReported(typology, objectId!).then((response) => {
        if (response.isAlreadyReported === false) {
          supportService.GetZendeskFormFields().then((response) => {
            setJsonObject(response.data);
            setSubTypologyOptions(response.data?.["Sous-typologie " + typology] || []);
          });
        } else {
          setAlreadyReported(response.isAlreadyReported!);
          setReportIds(response.ticketIds);
        }
      });
    } else handleConfirmAnywayClick();
  }, []);

  return alreadyReported ? (
    <Modal
      title={t("modal:title.issueAlreadyReported")}
      reference={ref}
      closeModal={handleCancelClick}
      width={800}
      footer={
        <Stack direction="row" justifyContent="space-between">
          <Button onClick={handleCancelClick}>{t("common:cancel")}</Button>
          <Button
            color="success"
            variant="outlined"
            onClick={handleConfirmAnywayClick}
            disabled={!isValid && !alreadyReported}
          >
            {t("common:confirm")}
          </Button>
        </Stack>
      }
    >
      <Stack spacing={2}>
        <Alert icon={<WarningIcon />} color="warning" sx={{ mr: 2 }}>
          {t("alert:warning.objectAlreadyReported", { objectId, reportIds })}
          <br />
          {t("alert:warning.reportIssueConfirm")}
        </Alert>
      </Stack>
    </Modal>
  ) : jsonObject ? (
    <Modal
      title={t("modal:title.reportIssue")}
      reference={ref}
      closeModal={handleCancelClick}
      width={800}
      footer={
        <Stack direction="row" justifyContent="space-between">
          <Button onClick={handleCancelClick}>{t("common:cancel")}</Button>
          <Button color="success" variant="outlined" onClick={handleConfirmClick} disabled={!isValid}>
            {t("common:confirm")}
          </Button>
        </Stack>
      }
    >
      <Stack spacing={2}>
        <FormAutocomplete
          multiple
          freeSolo
          popupIcon={<ExpandMoreRoundedIcon />}
          options={followersUsersOptions}
          placeholder={t("field:placeholder.selectFollowers")}
          label={t("field:label.setFollowers")}
          value={selectedFollowers}
          onChange={(v) => {
            if (v.length > 0 && !IsEmail(v[v.length - 1])) return;

            setSelectedFollowers(v ?? []);
          }}
          validationProps={{
            formId: "form-confirm",
            formSectionId: "Emails",
            fieldId: "Emails",
            required: false,
            initialState: false,
          }}
        />
        <Stack spacing={1}>
          <Typography component="span" fontWeight={500} color="text.primary">
            {t("common:typology")}
          </Typography>
          <Autocomplete
            value={typology}
            options={[]}
            sx={{ width: "100%" }}
            id={"typeFilter"}
            disabled={true}
            renderInput={(params) => <TextField sx={{ mt: "-24px" }} {...params} />}
          />
        </Stack>

        <FormAutocomplete
          popupIcon={<ExpandMoreRoundedIcon />}
          fullWidth
          options={subTypologyOptions || []}
          label={t("common:subTypology")}
          value={subTypologyValue}
          onChange={setSubTypologyValue}
          getOptionLabel={(value: any) => {
            return value.key;
          }}
          validationProps={{
            formId: "form-confirm",
            formSectionId: "sectionSubTypology",
            fieldId: "SubTypology",
            required: true,
            initialState: false,
          }}
        />
        <FormAutocomplete
          popupIcon={<ExpandMoreRoundedIcon />}
          fullWidth
          options={Object.keys(IssuePriority)}
          label={t("common:priority")}
          value={priority}
          onChange={setPriority}
          validationProps={{
            formId: "form-confirm",
            formSectionId: "Priority",
            fieldId: "Priority",
            required: false,
            initialState: false,
          }}
        />
        <FormInputText
          label={t("field:label.subject")}
          fullWidth={true}
          value={subject}
          placeholder={t("field:placeholder.subjectDot")}
          onChange={(e) => setSubject(e.target.value)}
          onReset={(e) => setSubject("")}
          validationProps={{
            formId: "form-confirm",
            formSectionId: "sectionSubject",
            fieldId: "Subject",
            required: true,
            initialState: false,
            minLength: 5,
          }}
        />
        <Stack spacing={2}>
          <Stack spacing={2} mb={2}>
            <InputLabel sx={{ mb: 1 }}>{t("common:description")}</InputLabel>
            <FormInputText
              multiline
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              placeholder={t("field:placeholder.comment")}
              rows={3}
              validationProps={{
                formId: "form-confirm",
                formSectionId: "sectionComment",
                fieldId: "comment",
                initialState: false,
                required: false,
                minLength: 10,
              }}
            />
          </Stack>
          {techInfos != null && techInfos?.length > 0 && (
            <Stack spacing={2} mb={2}>
              <InputLabel sx={{ mb: 1 }}>{t("field:label.technicalInfos")}</InputLabel>
              <Box
                sx={{
                  width: "100%",
                  height: "100px",
                  overflowY: "auto",
                  padding: "10px",
                  borderRadius: "10px",
                  bgcolor: "backgroundAlpha.main",
                }}
              >
                <Typography
                  variant="body1"
                  sx={{
                    whiteSpace: "pre-wrap",
                    fontFamily: "Arial, sans-serif",
                  }}
                >
                  {techInfos}
                </Typography>
              </Box>
            </Stack>
          )}
          <UploadCard
            title={t("card:title.dropTicketFileAttachment")}
            coverRounded
            userHasRightToDelete={true}
            uploadButtonInCard={false}
            context={UploadContext.SupportTicket}
            objectId={0}
            format={{
              id: 1,
              isUploaded: false,
              maxFileSizeMb: 10,
              name: "Document",
              userHasRighToDelete: false,
              acceptsFiles: {
                ["application/octet-stream"]: [".jpg", ".jpeg", ".png", ".pdf", ".doc", ".docx", ".xls", ".xlsx"],
              },
              description: t("card:description.ticketAttachment"),
              isUserDataMandatory: false,
              objectType: ObjectType.Document,
            }}
            onDroppedFiles={(files) => setFileData(files[0])}
          />
        </Stack>
      </Stack>
    </Modal>
  ) : (
    <Stack alignItems={"center"}>
      <CircularProgress />
    </Stack>
  );
});

export default ModalReportIssue;
